import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import StartSidebar from '../components/StartSidebar';
import PortalBackground from '../components/PortalBackground';

const ProgramCall = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [buttonEnabled, setButtonEnabled] = useState(false);
  const widgetRef = useRef(null);

  // Enable button after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setButtonEnabled(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  // Load Calendly script - widget will auto-initialize
  useEffect(() => {
    // Check if script already exists
    let script = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
    
    const initializeWidget = () => {
      if (widgetRef.current) {
        const widgetElement = widgetRef.current;
        // Check if widget is already initialized (has iframe)
        const hasIframe = widgetElement.querySelector('iframe');
        
        if (!hasIframe) {
          // Widget not initialized yet
          if (window.Calendly && window.Calendly.initInlineWidget) {
            // Use Calendly's initInlineWidget method
            try {
              window.Calendly.initInlineWidget({
                url: 'https://calendly.com/d/cwpc-n32-pqh/assessment-review-next-steps?primary_color=ff0000',
                parentElement: widgetElement
              });
              console.log('Calendly widget initialized manually');
            } catch (error) {
              console.error('Error initializing Calendly widget:', error);
            }
          } else {
            // Force re-initialization by temporarily removing and re-adding the class
            widgetElement.classList.remove('calendly-inline-widget');
            setTimeout(() => {
              widgetElement.classList.add('calendly-inline-widget');
              console.log('Triggered Calendly widget re-initialization');
            }, 50);
          }
        }
      }
    };
    
    let checkInterval = null;
    
    const loadCalendly = () => {
      if (!script) {
        script = document.createElement('script');
        script.src = 'https://assets.calendly.com/assets/external/widget.js';
        script.async = true;
        script.type = 'text/javascript';
        script.onload = () => {
          console.log('Calendly script loaded');
          // Wait a bit for Calendly to be ready, then initialize
          setTimeout(initializeWidget, 200);
        };
        script.onerror = () => {
          console.error('Failed to load Calendly script');
        };
        document.body.appendChild(script);
      } else if (window.Calendly) {
        // Script already loaded, manually initialize widget
        console.log('Calendly already loaded, initializing widget');
        setTimeout(initializeWidget, 100);
      } else {
        // Script exists but not loaded yet, wait for it
        checkInterval = setInterval(() => {
          if (window.Calendly) {
            if (checkInterval) clearInterval(checkInterval);
            setTimeout(initializeWidget, 100);
          }
        }, 100);
        
        // Cleanup after 5 seconds
        setTimeout(() => {
          if (checkInterval) clearInterval(checkInterval);
        }, 5000);
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(loadCalendly, 100);

    // Set up Calendly event listener for when a booking is made
    const handleCalendlyEvent = async (e) => {
      if (e.data && e.data.event === 'calendly.event_scheduled') {
        // Complete step 5 in Firebase when booking is confirmed
        if (currentUser) {
          try {
            // Get existing user data to preserve completed steps
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            let existingCompletedSteps = [];
            
            if (userDoc.exists()) {
              const userData = userDoc.data();
              if (userData.startFlowProgress && userData.startFlowProgress.completedSteps) {
                existingCompletedSteps = userData.startFlowProgress.completedSteps;
              }
            }
            
            // Add step 5 if not already completed
            if (!existingCompletedSteps.includes(5)) {
              existingCompletedSteps.push(5);
            }
            
            await updateDoc(doc(db, 'users', currentUser.uid), {
              startFlowProgress: {
                completedSteps: existingCompletedSteps,
                lastUpdated: new Date().toISOString()
              },
              lastUpdated: new Date().toISOString()
            });
          } catch (error) {
            console.error('Error completing step 5:', error);
          }
        }
      }
    };

    window.addEventListener('message', handleCalendlyEvent);

    return () => {
      clearTimeout(timer);
      if (checkInterval) clearInterval(checkInterval);
      window.removeEventListener('message', handleCalendlyEvent);
    };
  }, [currentUser]);

  const handleScheduled = async () => {
    if (!buttonEnabled || !currentUser) return;

    try {
      // Get existing user data to preserve completed steps
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      let existingCompletedSteps = [];
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.startFlowProgress && userData.startFlowProgress.completedSteps) {
          existingCompletedSteps = userData.startFlowProgress.completedSteps;
        }
      }
      
      // Add step 5 if not already completed
      if (!existingCompletedSteps.includes(5)) {
        existingCompletedSteps.push(5);
      }
      
      await updateDoc(doc(db, 'users', currentUser.uid), {
        startFlowProgress: {
          completedSteps: existingCompletedSteps,
          lastUpdated: new Date().toISOString()
        },
        lastUpdated: new Date().toISOString()
      });

      // Navigate to start dashboard
      navigate('/start/dashboard');
    } catch (error) {
      console.error('Error completing step 5:', error);
    }
  };

  return (
    <div className="relative min-h-screen">
      <PortalBackground />
      <div className="relative">
      <StartSidebar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white">
            Let's Talk Through Your Assessment
          </h1>
        </div>

        {/* Main Content - Full width */}
        <div className="mb-8">
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">
                Strategy & Assessment Call
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-6 max-w-3xl mx-auto">
                This is your opportunity to discuss your business goals, challenges, and how we can help you achieve sustainable growth. Our team will review your current situation and provide personalized recommendations tailored to your specific needs.
              </p>
              
              {/* Calendly Inline Widget */}
              <div 
                ref={widgetRef}
                className="calendly-inline-widget" 
                data-url="https://calendly.com/d/cwpc-n32-pqh/assessment-review-next-steps?primary_color=ff0000" 
                style={{ minWidth: '320px', height: '700px' }}
              ></div>
              
              {/* I've Scheduled Button */}
              <div className="mt-6">
                <button
                  onClick={handleScheduled}
                  disabled={!buttonEnabled}
                  className={`inline-flex items-center justify-center px-8 py-4 rounded-lg font-semibold text-lg transition-colors ${
                    buttonEnabled
                      ? 'bg-green-600 hover:bg-green-700 text-white cursor-pointer'
                      : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  }`}
                >
                  I've Scheduled
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Meeting Agenda - Full Width */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 text-center">
            Meeting Agenda
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {/* Agenda Item 1 */}
            <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-red-600 dark:text-red-400 text-lg font-semibold">1</span>
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Business Assessment</h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Review your current business model, challenges, and growth opportunities
              </p>
            </div>

            {/* Agenda Item 2 */}
            <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-red-600 dark:text-red-400 text-lg font-semibold">2</span>
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Strategy Development</h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Outline key growth strategies tailored to your specific needs and goals
              </p>
            </div>

            {/* Agenda Item 3 */}
            <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-red-600 dark:text-red-400 text-lg font-semibold">3</span>
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Implementation Plan</h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Outline next steps and timeline for executing your growth strategy
              </p>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default ProgramCall;
